import { NotifyHelper } from 'classes/helpers/notify.helper';
import lightFormat from 'date-fns/lightFormat';
import parseISO from 'date-fns/parseISO';
import { t } from 'i18next';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { LOCAL_DATE_FORMAT } from 'lib_ts/enums/datetime.enums';
import { ErrorVisibility } from 'lib_ts/enums/errors.enums';
import { IErrorType } from 'lib_ts/interfaces/common/i-error-type';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AdminErrorTypesService } from 'services/admin/error-types.service';
import { useAuthStore } from 'stores/use-auth-store';

const CONTEXT_NAME = 'AdminErrorTypesContext';

interface IFilters {
  errorIDs: string[];
  internal?: boolean;
  deleted?: boolean;
  visibility?: ErrorVisibility;
}

interface IOptionsDict {
  errorIDs: IOption[];
  _created: string[];
}

export interface IErrorTypesContext {
  filters: IFilters;
  mergeFilters: (value: Partial<IFilters>) => void;

  filtered: IErrorType[];
  loading: boolean;

  options: IOptionsDict;

  /** changes lastFetched date which auto-triggers data to be fetched */
  readonly refresh: () => void;

  readonly create: (payload: Partial<IErrorType>) => Promise<boolean>;
  readonly update: (payload: Partial<IErrorType>) => Promise<boolean>;
}

const DEFAULT: IErrorTypesContext = {
  filters: {
    errorIDs: [],
    internal: undefined,
    // default view shows only non-deleted errors
    deleted: false,
    // default view shows only cs errors
    visibility: ErrorVisibility.CustomerSupport,
  },
  mergeFilters: () => console.error(`${CONTEXT_NAME}: not init`),
  filtered: [],

  options: {
    errorIDs: [],
    _created: [],
  },

  loading: false,

  refresh: () => console.error(`${CONTEXT_NAME}: not init`),
  create: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
  update: async () =>
    new Promise(() => console.error(`${CONTEXT_NAME}: not init`)),
};

export const ErrorTypesContext = createContext(DEFAULT);

interface IProps {
  children: ReactNode;
}

export const ErrorTypesProvider: FC<IProps> = (props) => {
  const current = useAuthStore((s) => s.current);

  // changing this will trigger a reload
  const [lastFetched, setLastFetched] = useState(new Date());

  /** reload data to match session access */
  useEffect(() => setLastFetched(new Date()), [current.session]);

  const [types, setTypes] = useState<IErrorType[]>([]);

  const [filters, setFilters] = useState(DEFAULT.filters);

  const filtered = useMemo(
    () =>
      types
        .filter(
          (m) =>
            filters.errorIDs.length === 0 ||
            filters.errorIDs.includes(m.errorID)
        )
        .filter(
          (m) =>
            filters.internal === undefined || filters.internal === m.internal
        )
        .filter(
          (m) =>
            // all (nothing selected in the filter)
            filters.deleted === undefined ||
            // not-deleted (false OR undefined on the record)
            (!filters.deleted && !m.deleted) ||
            // deleted
            (filters.deleted && m.deleted)
        )
        .filter(
          (m) => !filters.visibility || filters.visibility === m.visibility
        ),
    [types, filters]
  );

  const options = useMemo(() => {
    if (!types) {
      return DEFAULT.options;
    }

    return {
      errorIDs: types.map((m) => {
        const o: IOption = {
          label: m.errorID,
          group: m.category,
          value: m.errorID,
        };

        return o;
      }),

      _created: ArrayHelper.unique(
        types.map((m) => lightFormat(parseISO(m._created), LOCAL_DATE_FORMAT))
      ),
    };
  }, [types]);

  const [loading, setLoading] = useState(DEFAULT.loading);

  const state: IErrorTypesContext = {
    filters: filters,

    mergeFilters: (v) => setFilters((prev) => ({ ...prev, ...v })),

    filtered: filtered,

    options: options,

    loading: loading,

    refresh: () => setLastFetched(new Date()),

    create: async (payload) => {
      try {
        if (!payload.errorID) {
          NotifyHelper.error({
            message_md: 'Please enter an error ID and try again.',
          });
          return false;
        }

        const existing = types.find((m) => m.errorID === payload.errorID);

        if (existing) {
          NotifyHelper.warning({
            message_md: `${
              existing.deleted ? 'A deleted error type' : 'An error type'
            } with \`errorID\` "${payload.errorID}" already exists!`,
          });
          return false;
        }

        setLoading(true);

        const result =
          await AdminErrorTypesService.getInstance().create(payload);

        if (!result.success) {
          throw new Error(result.error);
        }

        const newValue: IErrorType = result.data;
        setTypes((prev) => [...prev, newValue]);

        NotifyHelper.success({
          message_md: `"${newValue.errorID}" created!`,
        });

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error ? e.message : t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },

    update: async (payload) => {
      try {
        setLoading(true);

        const result =
          await AdminErrorTypesService.getInstance().update(payload);

        if (!result.success) {
          throw new Error(result.error);
        }

        const updated = result.data as IErrorType;

        setTypes((prev) =>
          prev.map((m) => (m._id === updated._id ? updated : m))
        );

        return true;
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: t('common.request-failed-msg'),
        });

        return false;
      } finally {
        setLoading(false);
      }
    },
  };

  /** fetch the data whenever lastFetched changes */
  useEffect(() => {
    const callback = async (): Promise<void> => {
      try {
        setLoading(true);

        const results = await AdminErrorTypesService.getInstance().getAll();

        results.sort((a, b) => StringHelper.safeSort(a.errorID, b.errorID));

        setTypes(results);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    callback();
  }, [lastFetched]);

  return (
    <ErrorTypesContext.Provider value={state}>
      {props.children}
    </ErrorTypesContext.Provider>
  );
};
